import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  getWorkOrder, 
  updateWorkOrder,
  archiveWorkOrder,
  deleteWorkOrder,
  getMessages, 
  createMessage,
  getPhotos,
  uploadPhoto,
  getDocuments,
  uploadDocument,
  getDocumentCategories,
  getBulletins,
  acknowledgeBulletin,
  getWeather,
  getEvents,
  askStewie,
  checkViolation,
  getCanvassRequests,
  createCanvassRequest,
  getProjectReport,
  getLessonRecommendations,
  downloadPdfReport,
  downloadSingleWorkOrderCsv,
  generateSafetyBriefing,
  downloadPhotosZip,
  getDocumentVersions,
  togglePinDocument,
  getAvailableCrews,
  assignCrewToWorkOrder,
  unassignCrewFromWorkOrder,
  generateJobPacket,
  getLogisticsRequests,
  recordDownload,
  getDailyProgressReports
} from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import CrewRequirementsPanel from '../components/CrewRequirementsPanel';
import RequestCrewCard from '../components/workorders/RequestCrewCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import DownloadDialog from '../components/DownloadDialog';
import DailyProgressForm from '../components/DailyProgressForm';
import DailyProgressHistory from '../components/DailyProgressHistory';
import { 
  ArrowLeft, 
  MapPin, 
  Calendar, 
  Users, 
  MessageSquare,
  Camera,
  FileText,
  AlertTriangle,
  Cloud,
  Clock,
  Send,
  Upload,
  Pin,
  Check,
  Download,
  RefreshCw,
  Bot,
  ClipboardList,
  ChevronRight,
  Shield,
  Lightbulb,
  Archive,
  ExternalLink,
  UserPlus,
  UserCheck,
  UserX,
  FileDown,
  Table2,
  FolderArchive,
  History,
  Trash2,
  Package,
  Loader2,
  Truck,
  Star,
  Eye,
  X,
  ClipboardCheck,
  Percent
} from 'lucide-react';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';

const WorkOrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isSupervisor, isCrewLeader } = useAuth();
  
  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Tab-specific states
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [photos, setPhotos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [bulletins, setBulletins] = useState([]);
  const [weather, setWeather] = useState(null);
  const [events, setEvents] = useState([]);
  const [stewieQuery, setStewieQuery] = useState('');
  const [stewieResponse, setStewieResponse] = useState(null);
  const [stewieLoading, setStewieLoading] = useState(false);
  const [canvassRequests, setCanvassRequests] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [safetyBriefing, setSafetyBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  
  // Upload states
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [photoTags, setPhotoTags] = useState('');
  const [photoNotes, setPhotoNotes] = useState('');
  const [docCategory, setDocCategory] = useState('Work Plan');
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [selectedDocVersions, setSelectedDocVersions] = useState(null);
  const [showVersionsDialog, setShowVersionsDialog] = useState(false);
  
  // Crew Assignment states
  const [availableCrews, setAvailableCrews] = useState([]);
  const [showAssignCrewDialog, setShowAssignCrewDialog] = useState(false);
  const [assigningCrew, setAssigningCrew] = useState(false);
  const [selectedCrew, setSelectedCrew] = useState(null);
  
  // Staffing Request states
  const [showStaffingRequestForm, setShowStaffingRequestForm] = useState(false);
  const [staffingRequest, setStaffingRequest] = useState({
    crew_leader: 0,
    a_mechanic: 0,
    b_mechanic: 0,
    helper: 0,
    shift_start: '',
    shift_end: '',
    notes: ''
  });
  const [submittingStaffing, setSubmittingStaffing] = useState(false);
  
  // Job Packet states
  const [generatingJobPacket, setGeneratingJobPacket] = useState(false);
  const [jobPacketData, setJobPacketData] = useState(null);
  const [showJobPacketDialog, setShowJobPacketDialog] = useState(false);
  
  // Lesson Viewer state
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [showLessonDialog, setShowLessonDialog] = useState(false);
  
  // Logistics Request states
  const [logisticsRequests, setLogisticsRequests] = useState([]);
  
  // Download Dialog states
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [downloadDialogConfig, setDownloadDialogConfig] = useState({
    fileType: 'pdf',
    defaultFilename: '',
    fileSize: null,
    onDownload: null
  });
  const [pendingDownload, setPendingDownload] = useState(null);
  
  // Daily Progress states
  const [showDailyProgressForm, setShowDailyProgressForm] = useState(false);
  const [todayProgressReport, setTodayProgressReport] = useState(null);
  const [checkingTodayReport, setCheckingTodayReport] = useState(false);

  useEffect(() => {
    fetchWorkOrder();
    fetchDocumentCategories();
  }, [id]);

  useEffect(() => {
    if (workOrder) {
      switch (activeTab) {
        case 'messages': fetchMessages(); break;
        case 'photos': fetchPhotos(); break;
        case 'documents': fetchDocuments(); break;
        case 'bulletins': fetchBulletins(); break;
        case 'weather': fetchWeather(); break;
        case 'timeline': fetchEvents(); break;
        case 'stewie': fetchRecommendations(); break;
        case 'crew': fetchCanvassRequests(); break;
        case 'progress': checkTodayProgress(); break;
      }
    }
  }, [activeTab, workOrder]);

  // Check if today's progress report exists
  useEffect(() => {
    if (workOrder) {
      checkTodayProgress();
    }
  }, [workOrder]);

  const checkTodayProgress = async () => {
    if (!workOrder) return;
    setCheckingTodayReport(true);
    try {
      const response = await getDailyProgressReports(id, 1);
      const reports = response.data.reports || [];
      if (reports.length > 0) {
        const today = new Date().toISOString().split('T')[0];
        const todayReport = reports.find(r => r.date === today);
        setTodayProgressReport(todayReport || null);
      } else {
        setTodayProgressReport(null);
      }
    } catch (error) {
      console.error('Failed to check today progress:', error);
    } finally {
      setCheckingTodayReport(false);
    }
  };

  const handleDailyProgressSuccess = (report) => {
    setTodayProgressReport(report);
    if (report.percent_complete !== undefined) {
      setWorkOrder(prev => ({ ...prev, percent_complete: report.percent_complete }));
    }
  };

  // Can submit daily progress - crew leader or supervisor
  const canSubmitDailyProgress = () => {
    if (!workOrder || !user) return false;
    const isAssignedLeader = workOrder.crew_leader_id === user.id;
    const isSupervisorRole = user.role === 'admin' || user.role === 'supervisor';
    const isActiveWO = ['in_progress', 'pending', 'assigned'].includes(workOrder.status);
    return (isAssignedLeader || isSupervisorRole) && isActiveWO;
  };

  const fetchWorkOrder = async () => {
    try {
      const response = await getWorkOrder(id);
      setWorkOrder(response.data);
    } catch (error) {
      console.error('Failed to fetch work order:', error);
      toast.error('Failed to load work order');
      navigate('/work-orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await getMessages(id);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const fetchPhotos = async () => {
    try {
      const response = await getPhotos(id);
      setPhotos(response.data);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await getDocuments(id);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const fetchDocumentCategories = async () => {
    try {
      const response = await getDocumentCategories();
      setDocumentCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchLogisticsRequests = async () => {
    try {
      const response = await getLogisticsRequests({ work_order_id: id });
      setLogisticsRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch logistics requests:', error);
    }
  };

  const handleGenerateJobPacket = async () => {
    setGeneratingJobPacket(true);
    setShowJobPacketDialog(true);  // Open dialog immediately to show loading
    try {
      const response = await generateJobPacket(id, {
        include_safety_brief: true,
        include_weather: true,
        include_past_projects: true,
        include_risk_assessment: true
      });
      setJobPacketData(response.data);
      toast.success('Job Packet generated successfully');
      // Record the download
      try {
        await recordDownload({
          file_type: 'job_packet',
          filename: `JobPacket_${workOrder?.wo_number || id}.json`,
          work_order_id: id,
          metadata: { generated_at: response.data?.generated_at }
        });
      } catch (e) { console.log('Failed to record download'); }
    } catch (error) {
      console.error('Failed to generate job packet:', error);
      toast.error('Failed to generate job packet');
      setShowJobPacketDialog(false);  // Close dialog on error
    } finally {
      setGeneratingJobPacket(false);
    }
  };

  const handleSaveJobPacket = async (format) => {
    if (!jobPacketData) return;
    
    const woNumber = workOrder?.wo_number || id;
    const timestamp = new Date().toISOString().split('T')[0];
    
    if (format === 'json') {
      // Save as JSON
      const jsonContent = JSON.stringify(jobPacketData, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const filename = `JobPacket_${woNumber}_${timestamp}.json`;
      
      // Use File System Access API if available
      if ('showSaveFilePicker' in window) {
        try {
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'JSON File',
              accept: { 'application/json': ['.json'] }
            }]
          });
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
          toast.success('Job Packet saved as JSON');
          await recordDownload({
            file_type: 'job_packet',
            filename: filename,
            work_order_id: id,
            file_size: blob.size
          });
          return;
        } catch (err) {
          if (err.name === 'AbortError') return;
        }
      }
      
      // Fallback download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Job Packet saved as JSON');
      
    } else if (format === 'pdf') {
      // Generate a printable HTML version and trigger print/save as PDF
      const printContent = generateJobPacketHTML(jobPacketData);
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
      toast.success('Print dialog opened - select "Save as PDF"');
    }
    
    try {
      await recordDownload({
        file_type: 'job_packet',
        filename: `JobPacket_${woNumber}_${timestamp}.${format}`,
        work_order_id: id
      });
    } catch (e) { console.log('Failed to record download'); }
  };

  const generateJobPacketHTML = (data) => {
    const wo = data.work_order || {};
    const safetyBrief = data.safety_brief?.briefing || 'No safety briefing available';
    const riskLevel = data.risk_assessment?.risk_level || 'N/A';
    const lessons = data.lessons_learned || [];
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Job Packet - ${wo.wo_number || 'Work Order'}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
          h1 { color: #0056D2; border-bottom: 2px solid #0056D2; padding-bottom: 10px; }
          h2 { color: #1e40af; margin-top: 30px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
          h3 { color: #059669; margin-top: 20px; }
          .section { margin: 20px 0; padding: 15px; background: #f8fafc; border-radius: 8px; }
          .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
          .info-item { padding: 8px; }
          .info-label { font-weight: bold; color: #64748b; font-size: 12px; }
          .info-value { color: #1e293b; }
          .safety-section { background: #fef3c7; border-left: 4px solid #f59e0b; }
          .risk-section { background: #fee2e2; border-left: 4px solid #ef4444; }
          .lesson-card { background: #ecfdf5; border-left: 4px solid #10b981; margin: 10px 0; padding: 15px; }
          .lesson-title { font-weight: bold; color: #065f46; }
          .lesson-content { margin-top: 10px; }
          .lesson-outcome { margin-top: 10px; background: #dbeafe; padding: 10px; border-radius: 4px; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #64748b; }
          @media print { body { margin: 20px; } }
        </style>
      </head>
      <body>
        <h1>📋 Job Packet</h1>
        
        <div class="section">
          <h2>Work Order Summary</h2>
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">WO Number</div>
              <div class="info-value">${wo.wo_number || 'N/A'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Status</div>
              <div class="info-value">${wo.status || 'N/A'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Location</div>
              <div class="info-value">${wo.location_name || 'N/A'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Project Type</div>
              <div class="info-value">${wo.project_type || 'N/A'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Asset Type</div>
              <div class="info-value">${wo.asset_type || 'N/A'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Equipment Type</div>
              <div class="info-value">${wo.equipment_type || 'N/A'}</div>
            </div>
          </div>
          <div class="info-item" style="margin-top: 15px;">
            <div class="info-label">Title</div>
            <div class="info-value">${wo.title || 'N/A'}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Description</div>
            <div class="info-value">${wo.description || 'N/A'}</div>
          </div>
        </div>
        
        <div class="section risk-section">
          <h2>⚠️ Risk Assessment</h2>
          <p><strong>Risk Level:</strong> ${riskLevel.toUpperCase()}</p>
          ${data.risk_assessment?.risks?.length > 0 ? `
            <p><strong>Identified Risks:</strong></p>
            <ul>${data.risk_assessment.risks.map(r => `<li>${r.message}</li>`).join('')}</ul>
          ` : ''}
        </div>
        
        <div class="section safety-section">
          <h2>🛡️ Safety Briefing</h2>
          <div style="white-space: pre-wrap;">${safetyBrief}</div>
        </div>
        
        ${lessons.length > 0 ? `
          <div class="section">
            <h2>💡 Lessons Learned (${lessons.length})</h2>
            ${lessons.map(l => `
              <div class="lesson-card">
                <div class="lesson-title">${l.title || 'Untitled'}</div>
                ${l.category ? `<div style="color: #059669; font-size: 12px;">${l.category}</div>` : ''}
                <div class="lesson-content">${l.content || ''}</div>
                ${l.outcome ? `<div class="lesson-outcome"><strong>Outcome/Resolution:</strong> ${l.outcome}</div>` : ''}
              </div>
            `).join('')}
          </div>
        ` : ''}
        
        <div class="footer">
          <p>Generated: ${new Date().toLocaleString()}</p>
          <p>Work Order ID: ${wo.id || id}</p>
        </div>
      </body>
      </html>
    `;
  };

  const fetchBulletins = async () => {
    try {
      const response = await getBulletins({ work_order_id: id });
      setBulletins(response.data);
    } catch (error) {
      console.error('Failed to fetch bulletins:', error);
    }
  };

  const fetchWeather = async () => {
    try {
      const response = await getWeather(id);
      setWeather(response.data);
    } catch (error) {
      console.error('Failed to fetch weather:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await getEvents(id);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const fetchCanvassRequests = async () => {
    try {
      const response = await getCanvassRequests(id);
      setCanvassRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch canvass requests:', error);
    }
  };

  const fetchAvailableCrews = async () => {
    try {
      const response = await getAvailableCrews();
      setAvailableCrews(response.data);
    } catch (error) {
      console.error('Failed to fetch available crews:', error);
    }
  };

  const handleAssignCrew = async () => {
    if (!selectedCrew) {
      toast.error('Please select a crew to assign');
      return;
    }
    
    setAssigningCrew(true);
    try {
      await assignCrewToWorkOrder(id, {
        crew_id: selectedCrew.id,
        qc_id: selectedCrew.qc_id,
        crew_name: selectedCrew.name,
        members: selectedCrew.members || []
      });
      toast.success(`Crew "${selectedCrew.name}" assigned successfully`);
      setShowAssignCrewDialog(false);
      setSelectedCrew(null);
      fetchWorkOrder();
      fetchCanvassRequests();
    } catch (error) {
      toast.error('Failed to assign crew');
    } finally {
      setAssigningCrew(false);
    }
  };

  const handleUnassignCrew = async () => {
    if (!window.confirm('Are you sure you want to unassign the current crew?')) return;
    
    try {
      await unassignCrewFromWorkOrder(id);
      toast.success('Crew unassigned');
      fetchWorkOrder();
    } catch (error) {
      toast.error('Failed to unassign crew');
    }
  };

  const handleSubmitStaffingRequest = async () => {
    const totalPositions = staffingRequest.crew_leader + staffingRequest.a_mechanic + 
                          staffingRequest.b_mechanic + staffingRequest.helper;
    
    if (totalPositions === 0) {
      toast.error('Please select at least one position');
      return;
    }
    
    setSubmittingStaffing(true);
    try {
      const positions = [];
      if (staffingRequest.crew_leader > 0) {
        for (let i = 0; i < staffingRequest.crew_leader; i++) positions.push('Crew Leader');
      }
      if (staffingRequest.a_mechanic > 0) {
        for (let i = 0; i < staffingRequest.a_mechanic; i++) positions.push('A Mechanic');
      }
      if (staffingRequest.b_mechanic > 0) {
        for (let i = 0; i < staffingRequest.b_mechanic; i++) positions.push('B Mechanic');
      }
      if (staffingRequest.helper > 0) {
        for (let i = 0; i < staffingRequest.helper; i++) positions.push('Helper');
      }
      
      await createCanvassRequest({
        work_order_id: id,
        positions_needed: {
          crew_leader: staffingRequest.crew_leader,
          a_mechanic: staffingRequest.a_mechanic,
          b_mechanic: staffingRequest.b_mechanic,
          helper: staffingRequest.helper
        },
        required_positions: positions,
        shift_start: staffingRequest.shift_start || new Date().toISOString(),
        shift_end: staffingRequest.shift_end || new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(),
        location: workOrder.location_name,
        notes: staffingRequest.notes
      });
      
      toast.success('Staffing request submitted to Quick Canvass');
      setShowStaffingRequestForm(false);
      setStaffingRequest({
        crew_leader: 0,
        a_mechanic: 0,
        b_mechanic: 0,
        helper: 0,
        shift_start: '',
        shift_end: '',
        notes: ''
      });
      fetchCanvassRequests();
    } catch (error) {
      toast.error('Failed to submit staffing request');
    } finally {
      setSubmittingStaffing(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const response = await getLessonRecommendations(id);
      setRecommendations(response.data);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    try {
      await createMessage({
        work_order_id: id,
        content: newMessage,
        mentions: [],
        attachment_urls: []
      });
      setNewMessage('');
      fetchMessages();
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handlePhotoUpload = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setUploadingPhoto(true);
    
    try {
      for (const file of acceptedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('work_order_id', id);
        formData.append('tags', photoTags);
        formData.append('notes', photoNotes);
        await uploadPhoto(formData);
      }
      toast.success('Photos uploaded successfully');
      setPhotoTags('');
      setPhotoNotes('');
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to upload photos');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDocUpload = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setUploadingDoc(true);
    
    try {
      for (const file of acceptedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('work_order_id', id);
        formData.append('category', docCategory);
        formData.append('is_pinned', 'false');
        await uploadDocument(formData);
      }
      toast.success('Documents uploaded successfully');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to upload documents');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleAcknowledgeBulletin = async (bulletinId) => {
    try {
      await acknowledgeBulletin(bulletinId);
      toast.success('Bulletin acknowledged');
      fetchBulletins();
    } catch (error) {
      toast.error('Failed to acknowledge bulletin');
    }
  };

  const handleAskStewie = async (e) => {
    e.preventDefault();
    if (!stewieQuery.trim()) return;
    
    setStewieLoading(true);
    try {
      const response = await askStewie(stewieQuery, id);
      setStewieResponse(response.data);
    } catch (error) {
      toast.error('Failed to get response from Ask Stewie');
    } finally {
      setStewieLoading(false);
    }
  };

  const handleGenerateSafetyBriefing = async () => {
    setBriefingLoading(true);
    try {
      const response = await generateSafetyBriefing(id);
      setSafetyBriefing(response.data);
      toast.success('Safety briefing generated');
    } catch (error) {
      toast.error('Failed to generate safety briefing');
    } finally {
      setBriefingLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      await updateWorkOrder(id, { status: newStatus });
      toast.success('Status updated');
      fetchWorkOrder();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleGenerateReport = async () => {
    try {
      const response = await getProjectReport(id);
      // In production, this would generate a PDF
      toast.success('Report generated');
      console.log('Report data:', response.data);
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };

  // Show download dialog for PDF
  const handleDownloadPdfClick = () => {
    const defaultFilename = `WorkOrder_${workOrder?.wo_number || id}_Report`;
    setDownloadDialogConfig({
      fileType: 'pdf',
      defaultFilename,
      fileSize: null,
      onDownload: async (customFilename, returnBlob = false) => {
        return await executeDownloadPdf(customFilename, returnBlob);
      }
    });
    setShowDownloadDialog(true);
  };

  const executeDownloadPdf = async (customFilename, returnBlob = false) => {
    try {
      const response = await downloadPdfReport(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const filename = `${customFilename}.pdf`;
      
      // If returnBlob is true, return the blob for "Save As" dialog
      if (returnBlob) {
        // Record the download
        try {
          await recordDownload({
            file_type: 'pdf_report',
            filename: filename,
            work_order_id: id,
            file_size: blob.size
          });
        } catch (e) { console.log('Failed to record download'); }
        toast.success('PDF report saved');
        return blob;
      }
      
      // Otherwise do regular download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF report saved to computer');
      try {
        await recordDownload({
          file_type: 'pdf_report',
          filename: filename,
          work_order_id: id,
          file_size: blob.size
        });
      } catch (e) { console.log('Failed to record download'); }
    } catch (error) {
      toast.error('Failed to generate PDF report');
      throw error;
    }
  };

  // Legacy direct download (used by Job Packet dialog)
  const handleDownloadPdf = async () => {
    try {
      toast.info('Generating PDF report...');
      await executeDownloadPdf(`WorkOrder_${workOrder?.wo_number || id}_Report`);
    } catch (error) {
      // Error already handled
    }
  };

  // Show download dialog for CSV
  const handleDownloadCsvClick = () => {
    const defaultFilename = `WorkOrder_${workOrder?.wo_number || id}_Details`;
    setDownloadDialogConfig({
      fileType: 'csv',
      defaultFilename,
      fileSize: null,
      onDownload: async (customFilename, returnBlob = false) => {
        return await executeDownloadCsv(customFilename, returnBlob);
      }
    });
    setShowDownloadDialog(true);
  };

  const executeDownloadCsv = async (customFilename, returnBlob = false) => {
    try {
      const response = await downloadSingleWorkOrderCsv(id);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const filename = `${customFilename}.csv`;
      
      // If returnBlob is true, return the blob for "Save As" dialog
      if (returnBlob) {
        try {
          await recordDownload({
            file_type: 'csv_export',
            filename: filename,
            work_order_id: id,
            file_size: blob.size
          });
        } catch (e) { console.log('Failed to record download'); }
        toast.success('CSV export saved');
        return blob;
      }
      
      // Otherwise do regular download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('CSV export saved to computer');
      try {
        await recordDownload({
          file_type: 'csv_export',
          filename: filename,
          work_order_id: id,
          file_size: blob.size
        });
      } catch (e) { console.log('Failed to record download'); }
    } catch (error) {
      toast.error('Failed to generate CSV export');
      throw error;
    }
  };

  // Legacy direct download
  const handleDownloadCsv = async () => {
    try {
      toast.info('Generating CSV export...');
      await executeDownloadCsv(`WorkOrder_${workOrder?.wo_number || id}_Details`);
    } catch (error) {
      // Error already handled
    }
  };

  const handleDownloadPhotosZip = async () => {
    if (photos.length === 0) {
      toast.error('No photos to download');
      return;
    }
    try {
      setDownloadingZip(true);
      toast.info('Creating ZIP archive...');
      const response = await downloadPhotosZip(id);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Photos_${workOrder?.wo_number || id}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Photos ZIP downloaded');
    } catch (error) {
      toast.error('Failed to create ZIP archive');
    } finally {
      setDownloadingZip(false);
    }
  };

  const handleViewDocVersions = async (doc) => {
    try {
      const response = await getDocumentVersions(id, doc.filename);
      setSelectedDocVersions({ filename: doc.filename, versions: response.data });
      setShowVersionsDialog(true);
    } catch (error) {
      toast.error('Failed to load document versions');
    }
  };

  const handleTogglePin = async (doc) => {
    try {
      await togglePinDocument(doc.id, !doc.is_pinned);
      toast.success(doc.is_pinned ? 'Document unpinned' : 'Document pinned');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to update pin status');
    }
  };

  const handleArchive = async () => {
    try {
      await archiveWorkOrder(id);
      toast.success('Work Order archived');
      navigate('/archive');
    } catch (error) {
      toast.error('Failed to archive work order');
    }
  };

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setDeleting(true);
    try {
      await deleteWorkOrder(id);
      toast.success('Work Order deleted permanently');
      navigate('/work-orders');
    } catch (error) {
      toast.error('Failed to delete work order');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
      setDeleteConfirmText('');
    }
  };

  const { getRootProps: getPhotoRootProps, getInputProps: getPhotoInputProps } = useDropzone({
    onDrop: handlePhotoUpload,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    multiple: true
  });

  const { getRootProps: getDocRootProps, getInputProps: getDocInputProps } = useDropzone({
    onDrop: handleDocUpload,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    multiple: true
  });

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-800 border-amber-300',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
      completed: 'bg-green-100 text-green-800 border-green-300',
      closed: 'bg-slate-100 text-slate-800 border-slate-300'
    };
    return colors[status] || colors.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#0056D2]" />
      </div>
    );
  }

  if (!workOrder) return null;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="work-order-detail">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <Button variant="ghost" onClick={() => navigate('/work-orders')} className="mb-2 -ml-2">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Work Orders
          </Button>
          <div className="flex items-center gap-3 mb-2">
            <span className="font-mono text-lg text-[#0056D2] font-bold">{workOrder.wo_number}</span>
            <Badge className={getStatusColor(workOrder.status)}>
              {workOrder.status?.replace('_', ' ')}
            </Badge>
          </div>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight break-words" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {workOrder.title}
          </h1>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {(isSupervisor() || isCrewLeader()) && (
            <Select value={workOrder.status} onValueChange={handleStatusUpdate}>
              <SelectTrigger className="w-32 sm:w-36" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="whitespace-nowrap" data-testid="export-menu-btn">
                <Download className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Export</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleDownloadPdfClick} data-testid="pdf-download-btn">
                <FileDown className="w-4 h-4 mr-2 text-red-600" />
                Download PDF Report
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDownloadCsvClick} data-testid="csv-download-btn">
                <Table2 className="w-4 h-4 mr-2 text-green-600" />
                Export to CSV
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleGenerateJobPacket} disabled={generatingJobPacket} data-testid="job-packet-btn">
                <Package className="w-4 h-4 mr-2 text-purple-600" />
                {generatingJobPacket ? 'Generating...' : 'Generate Job Packet'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleGenerateReport} data-testid="view-report-btn">
                <FileText className="w-4 h-4 mr-2 text-blue-600" />
                View Report Data
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {isSupervisor() && (
            <Button variant="outline" onClick={handleArchive} className="whitespace-nowrap" data-testid="archive-wo-btn">
              <Archive className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Archive</span>
            </Button>
          )}
          {isAdmin() && (
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteDialog(true)} 
              className="whitespace-nowrap text-red-600 border-red-300 hover:bg-red-50" 
              data-testid="delete-wo-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Delete</span>
            </Button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete Work Order
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-red-900/30 border border-red-700 rounded-lg">
              <p className="text-sm text-red-400 font-medium">
                ⚠️ This action is permanent and cannot be undone.
              </p>
              <p className="text-sm text-red-300 mt-2">
                All associated data including messages, photos, documents, and history will be permanently deleted.
              </p>
            </div>
            
            <div>
              <Label className="text-sm text-slate-300">
                Type <strong className="text-red-400">DELETE</strong> to confirm:
              </Label>
              <Input 
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE here"
                className="mt-1 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-red-500"
                data-testid="delete-confirm-input"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button 
                variant="outline"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeleteConfirmText('');
                }}
                className="border-slate-600 hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleDelete}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-btn"
              >
                {deleting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Permanently
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Job Packet Dialog */}
      <Dialog open={showJobPacketDialog} onOpenChange={setShowJobPacketDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-purple-400 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Job Packet - {workOrder?.wo_number}
            </DialogTitle>
          </DialogHeader>
          {generatingJobPacket ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
              <span className="ml-3 text-purple-300">Generating Job Packet...</span>
            </div>
          ) : jobPacketData ? (
            <div className="space-y-6">
              {/* Work Order Summary */}
              <div className="p-4 bg-slate-800 rounded-lg border border-slate-700">
                <h3 className="font-semibold text-purple-400 mb-2 flex items-center gap-2">
                  <ClipboardList className="w-4 h-4" />
                  Work Order Summary
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm text-slate-300">
                  <div><strong className="text-white">WO #:</strong> {jobPacketData.work_order?.wo_number}</div>
                  <div><strong className="text-white">Status:</strong> {jobPacketData.work_order?.status}</div>
                  <div><strong className="text-white">Location:</strong> {jobPacketData.work_order?.location_name}</div>
                  <div><strong className="text-white">Project Type:</strong> {jobPacketData.work_order?.project_type}</div>
                  <div className="col-span-2"><strong className="text-white">Title:</strong> {jobPacketData.work_order?.title}</div>
                </div>
              </div>

              {/* Safety Briefing */}
              {jobPacketData.safety_brief && (
                <div className="p-4 bg-amber-900/30 rounded-lg border border-amber-700">
                  <h3 className="font-semibold text-amber-400 mb-2 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Safety Briefing
                  </h3>
                  <div className="prose prose-sm max-w-none text-amber-200 whitespace-pre-wrap">
                    {jobPacketData.safety_brief?.briefing || 'No safety briefing available'}
                  </div>
                </div>
              )}

              {/* Risk Assessment */}
              {jobPacketData.risk_assessment && (
                <div className="p-4 bg-red-900/30 rounded-lg border border-red-700">
                  <h3 className="font-semibold text-red-400 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Risk Assessment
                  </h3>
                  <div className="space-y-2">
                    <Badge className={`${jobPacketData.risk_assessment.risk_level === 'high' ? 'bg-red-600' : jobPacketData.risk_assessment.risk_level === 'medium' ? 'bg-yellow-500' : 'bg-green-500'} text-white`}>
                      Risk Level: {jobPacketData.risk_assessment.risk_level?.toUpperCase()}
                    </Badge>
                    {jobPacketData.risk_assessment.risks?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-red-400">Identified Risks:</p>
                        <ul className="list-disc list-inside text-sm text-red-300">
                          {jobPacketData.risk_assessment.risks.map((risk, i) => (
                            <li key={i}>{risk.message}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {jobPacketData.risk_assessment.warnings?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-yellow-400">Warnings:</p>
                        <ul className="list-disc list-inside text-sm text-yellow-300">
                          {jobPacketData.risk_assessment.warnings.map((warn, i) => (
                            <li key={i}>{warn.message}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Weather */}
              {jobPacketData.weather && (
                <div className="p-4 bg-blue-900/30 rounded-lg border border-blue-700">
                  <h3 className="font-semibold text-blue-400 mb-2 flex items-center gap-2">
                    <Cloud className="w-4 h-4" />
                    Weather Conditions
                  </h3>
                  <div className="text-sm text-blue-300">
                    <p><strong className="text-white">Temperature:</strong> {jobPacketData.weather.temperature}°F</p>
                    <p><strong className="text-white">Conditions:</strong> {jobPacketData.weather.conditions}</p>
                    <p><strong className="text-white">Wind:</strong> {jobPacketData.weather.wind_speed} mph</p>
                  </div>
                </div>
              )}

              {/* Past Projects / Lessons Learned */}
              {jobPacketData.past_projects && jobPacketData.past_projects.lessons_learned?.length > 0 && (
                <div className="p-4 bg-green-900/30 rounded-lg border border-green-700">
                  <h3 className="font-semibold text-green-400 mb-2 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    Suggested Guidance (Past Projects)
                  </h3>
                  <ul className="list-disc list-inside text-sm text-green-300 space-y-1">
                    {jobPacketData.past_projects.lessons_learned.slice(0, 5).map((lesson, i) => (
                      <li key={i}><strong className="text-white">{lesson.title}:</strong> {lesson.content?.substring(0, 150)}...</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Lessons Learned */}
              {jobPacketData.lessons_learned && jobPacketData.lessons_learned.length > 0 && (
                <div className="p-4 bg-emerald-900/30 rounded-lg border border-emerald-700">
                  <h3 className="font-semibold text-emerald-400 mb-3 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    Lessons Learned ({jobPacketData.lessons_learned.length})
                  </h3>
                  <div className="space-y-2">
                    {jobPacketData.lessons_learned.map((lesson, i) => (
                      <div key={lesson.id || i} className="flex items-center justify-between p-3 bg-slate-800 rounded-md border border-emerald-700 hover:bg-slate-700 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <Lightbulb className="w-4 h-4 text-emerald-400 shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium text-emerald-300 truncate">{lesson.title}</p>
                            {lesson.category && (
                              <p className="text-xs text-emerald-500">{lesson.category}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedLesson(lesson);
                            setShowLessonDialog(true);
                          }}
                          className="shrink-0 text-emerald-400 border-emerald-600 hover:bg-emerald-900/50"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {jobPacketData.lessons_learned && jobPacketData.lessons_learned.length === 0 && (
                <div className="p-4 bg-slate-800 rounded-lg border border-slate-700">
                  <p className="text-sm text-slate-400 text-center">
                    No specific lessons learned found for this work order&apos;s subject matter.
                  </p>
                </div>
              )}

              {/* Crew Info */}
              {jobPacketData.work_order?.crew_size && Object.keys(jobPacketData.work_order.crew_size).length > 0 && (
                <div className="p-4 bg-slate-800 rounded-lg border border-slate-700">
                  <h3 className="font-semibold text-slate-300 mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Crew Requirements
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(jobPacketData.work_order.crew_size).map(([role, count]) => (
                      <Badge key={role} variant="secondary" className="bg-slate-700 text-slate-300">{role.replace('_', ' ')}: {count}</Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3 pt-4 border-t border-slate-700">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-slate-400">Save Job Packet for future access:</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    onClick={() => handleSaveJobPacket('pdf')} 
                    className="bg-red-600 hover:bg-red-700 flex-1"
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    Save as PDF
                  </Button>
                  <Button 
                    onClick={() => handleSaveJobPacket('json')} 
                    variant="outline"
                    className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Save as JSON
                  </Button>
                </div>
                <div className="flex justify-end">
                  <Button variant="ghost" onClick={() => setShowJobPacketDialog(false)} className="text-slate-300 hover:bg-slate-700">
                    Close
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Quick Info Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 min-w-0">
              <MapPin className="w-5 h-5 text-[#0056D2] flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-slate-500">Location</p>
                <p className="font-medium text-sm truncate" title={workOrder.location_name}>{workOrder.location_name}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 min-w-0">
              <Calendar className="w-5 h-5 text-[#0056D2] flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-slate-500">Schedule</p>
                <p className="font-medium text-sm truncate">{workOrder.start_date || 'TBD'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 min-w-0">
              <Users className="w-5 h-5 text-[#0056D2] flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-slate-500">Crew Size</p>
                <p className="font-medium text-sm">{(workOrder.assigned_crew?.length || 0) + 1}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 min-w-0">
              <ClipboardList className="w-5 h-5 text-[#0056D2] flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-slate-500">Type</p>
                <p className="font-medium text-sm truncate" title={workOrder.project_type || 'N/A'}>{workOrder.project_type || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 p-1 overflow-x-auto">
            <TabsTrigger value="overview" className="text-xs sm:text-sm whitespace-nowrap" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="progress" className="text-xs sm:text-sm whitespace-nowrap" data-testid="tab-progress">
              <ClipboardCheck className="w-3 h-3 mr-1 hidden sm:inline" />
              Progress
            </TabsTrigger>
            <TabsTrigger value="messages" className="text-xs sm:text-sm whitespace-nowrap" data-testid="tab-messages">Messages</TabsTrigger>
            <TabsTrigger value="photos" className="text-xs sm:text-sm whitespace-nowrap" data-testid="tab-photos">Photos</TabsTrigger>
            <TabsTrigger value="documents" className="text-xs sm:text-sm whitespace-nowrap" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="bulletins" className="text-xs sm:text-sm whitespace-nowrap" data-testid="tab-bulletins">Bulletins</TabsTrigger>
            <TabsTrigger value="weather" className="text-xs sm:text-sm whitespace-nowrap" data-testid="tab-weather">Weather</TabsTrigger>
            <TabsTrigger value="stewie" className="text-xs sm:text-sm whitespace-nowrap" data-testid="tab-stewie">Ask Stewie</TabsTrigger>
            <TabsTrigger value="crew" className="text-xs sm:text-sm whitespace-nowrap" data-testid="tab-crew">Crew / Canvass</TabsTrigger>
            <TabsTrigger value="timeline" className="text-xs sm:text-sm whitespace-nowrap" data-testid="tab-timeline">Timeline</TabsTrigger>
          </TabsList>
          
          {/* Job Packet Button - Prominent placement */}
          <Button 
            onClick={handleGenerateJobPacket}
            disabled={generatingJobPacket}
            className="bg-purple-600 hover:bg-purple-700 text-white whitespace-nowrap"
            data-testid="generate-job-packet-btn"
          >
            {generatingJobPacket ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Package className="w-4 h-4 mr-2" />
                Generate Job Packet
              </>
            )}
          </Button>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Project Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {workOrder.description && (
                    <div>
                      <Label className="text-slate-500">Description</Label>
                      <p className="mt-1">{workOrder.description}</p>
                    </div>
                  )}
                  {workOrder.scope && (
                    <div>
                      <Label className="text-slate-500">Scope</Label>
                      <p className="mt-1">{workOrder.scope}</p>
                    </div>
                  )}
                  {workOrder.safety_notes && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-5 h-5 text-amber-600" />
                        <Label className="text-amber-800 font-semibold">Safety Notes</Label>
                      </div>
                      <p className="text-amber-900">{workOrder.safety_notes}</p>
                    </div>
                  )}
                  {workOrder.outage_window && (
                    <div>
                      <Label className="text-slate-500">Outage Window</Label>
                      <p className="mt-1 font-mono text-[#0056D2]">{workOrder.outage_window}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recommended Practices */}
              {recommendations.length > 0 && (
                <Card className="border-l-4 border-l-green-500">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-green-500" />
                      Recommended Practices
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {recommendations.map((lesson) => (
                        <div key={lesson.id} className="p-3 bg-green-50 rounded-lg">
                          <h4 className="font-semibold text-green-800">{lesson.title}</h4>
                          <p className="text-sm text-green-700 mt-1 line-clamp-2">{lesson.content}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="space-y-6">
              {/* Request Crew Card - For Supervisors/Admins */}
              {isSupervisor() && (
                <RequestCrewCard 
                  workOrderId={id}
                  workOrder={workOrder}
                  onUpdate={fetchWorkOrder}
                />
              )}

              {/* Tags */}
              {workOrder.tags?.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Tags</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {workOrder.tags.map((tag, i) => (
                        <Badge key={i} variant="outline">{tag}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Asset Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Asset Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {workOrder.asset_type && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Asset Type</span>
                      <span className="font-medium">{workOrder.asset_type}</span>
                    </div>
                  )}
                  {workOrder.stations?.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Stations</span>
                      <span className="font-medium">{workOrder.stations.join(', ')}</span>
                    </div>
                  )}
                  {workOrder.bays?.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Bays</span>
                      <span className="font-medium">{workOrder.bays.join(', ')}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Daily Progress Tab */}
        <TabsContent value="progress">
          <div className="space-y-4">
            {/* Today's Status Card */}
            <Card className={todayProgressReport ? 'border-green-200 bg-green-50/50' : 'border-amber-200 bg-amber-50/50'}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {todayProgressReport ? (
                      <>
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <Check className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-green-800">Today&apos;s Report Submitted</p>
                          <p className="text-sm text-green-600">
                            {new Date(todayProgressReport.submitted_at).toLocaleTimeString()} • {todayProgressReport.percent_complete}% complete
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                          <Clock className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-amber-800">Daily Progress Report Needed</p>
                          <p className="text-sm text-amber-600">
                            Submit before 1:00 PM to be on time
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  {canSubmitDailyProgress() && (
                    <Button
                      onClick={() => setShowDailyProgressForm(true)}
                      disabled={checkingTodayReport}
                      className={todayProgressReport ? 'bg-slate-600 hover:bg-slate-700' : 'bg-blue-600 hover:bg-blue-700'}
                    >
                      <ClipboardCheck className="w-4 h-4 mr-2" />
                      {todayProgressReport ? 'View / Edit' : 'Submit Progress'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Progress History */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Daily Progress History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DailyProgressHistory workOrderId={id} woNumber={workOrder?.wo_number} />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Project Messages
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px] mb-4">
                {messages.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div 
                        key={msg.id} 
                        className={`p-3 rounded-lg ${
                          msg.sender_id === user?.id 
                            ? 'bg-[#0056D2] text-white ml-8' 
                            : 'bg-slate-100 mr-8'
                        }`}
                        data-testid={`message-${msg.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm">{msg.sender_name}</span>
                          <span className="text-xs opacity-75">
                            {new Date(msg.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm">{msg.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
              <form onSubmit={handleSendMessage} className="flex gap-2">
                <Input
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  data-testid="message-input"
                />
                <Button type="submit" className="bg-[#0056D2]" data-testid="send-message-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Photos Tab */}
        <TabsContent value="photos">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5" />
                Project Photos
              </CardTitle>
              {photos.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleDownloadPhotosZip}
                  disabled={downloadingZip}
                  data-testid="download-photos-zip-btn"
                >
                  {downloadingZip ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FolderArchive className="w-4 h-4 mr-2" />
                  )}
                  Download All ({photos.length})
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {/* Upload Area */}
              <div 
                {...getPhotoRootProps()} 
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-[#0056D2] transition-colors mb-6"
              >
                <input {...getPhotoInputProps()} data-testid="photo-upload-input" />
                <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                <p className="text-slate-600">Drag & drop photos here, or click to select</p>
                <p className="text-sm text-slate-400 mt-1">Supports: JPEG, PNG, WebP</p>
              </div>

              <div className="flex gap-4 mb-6">
                <Input
                  placeholder="Tags (comma separated)"
                  value={photoTags}
                  onChange={(e) => setPhotoTags(e.target.value)}
                  className="flex-1"
                />
                <Input
                  placeholder="Notes"
                  value={photoNotes}
                  onChange={(e) => setPhotoNotes(e.target.value)}
                  className="flex-1"
                />
              </div>

              {/* Photo Grid */}
              {photos.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Camera className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No photos uploaded yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {photos.map((photo) => (
                    <div 
                      key={photo.id} 
                      className="relative group aspect-square rounded-lg overflow-hidden bg-slate-100"
                      data-testid={`photo-${photo.id}`}
                    >
                      <img 
                        src={photo.url} 
                        alt={photo.filename}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                        <div className="text-white text-xs">
                          <p className="truncate">{photo.filename}</p>
                          {photo.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {photo.tags.map((tag, i) => (
                                <span key={i} className="px-1 bg-white/20 rounded">{tag}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Documents Library
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Upload Area */}
              <div className="flex gap-4 mb-6">
                <Select value={docCategory} onValueChange={setDocCategory}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {documentCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div 
                  {...getDocRootProps()} 
                  className="flex-1 border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-[#0056D2] transition-colors"
                >
                  <input {...getDocInputProps()} data-testid="doc-upload-input" />
                  <p className="text-slate-600">
                    <Upload className="w-4 h-4 inline mr-2" />
                    Drop files or click to upload
                  </p>
                </div>
              </div>

              {/* Documents List */}
              {documents.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No documents uploaded yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div 
                      key={doc.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        doc.is_pinned ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'
                      }`}
                      data-testid={`document-${doc.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {doc.is_pinned && <Pin className="w-4 h-4 text-amber-600 flex-shrink-0" />}
                        <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{doc.filename}</p>
                          <p className="text-xs text-slate-500">
                            {doc.category} • v{doc.version} • {doc.uploader_name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Badge variant="outline" className="hidden sm:inline-flex">{doc.category}</Badge>
                        {doc.version > 1 && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleViewDocVersions(doc)}
                            title="View version history"
                          >
                            <History className="w-4 h-4" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleTogglePin(doc)}
                          title={doc.is_pinned ? 'Unpin document' : 'Pin document'}
                        >
                          <Pin className={`w-4 h-4 ${doc.is_pinned ? 'text-amber-600 fill-amber-200' : ''}`} />
                        </Button>
                        <Button variant="ghost" size="sm" asChild>
                          <a href={doc.url} download={doc.filename} title="Download">
                            <Download className="w-4 h-4" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Document Version History Dialog */}
              <Dialog open={showVersionsDialog} onOpenChange={setShowVersionsDialog}>
                <DialogContent className="max-w-md bg-slate-900 border-slate-700 text-white">
                  <DialogHeader>
                    <DialogTitle className="text-green-400 flex items-center gap-2">
                      <History className="w-5 h-5" />
                      Version History
                    </DialogTitle>
                  </DialogHeader>
                  {selectedDocVersions && (
                    <div className="space-y-3">
                      <p className="text-sm text-slate-300 font-medium truncate">
                        {selectedDocVersions.filename}
                      </p>
                      <ScrollArea className="h-[300px]">
                        <div className="space-y-2">
                          {selectedDocVersions.versions.map((v) => (
                            <div 
                              key={v.id}
                              className="flex items-center justify-between p-3 bg-slate-800 rounded-lg border border-slate-700"
                            >
                              <div>
                                <p className="font-medium text-green-400">Version {v.version}</p>
                                <p className="text-xs text-slate-400">
                                  {v.uploader_name} • {new Date(v.created_at).toLocaleString()}
                                </p>
                              </div>
                              <Button variant="ghost" size="sm" asChild className="text-slate-300 hover:bg-slate-700">
                                <a href={v.url} download={`v${v.version}_${v.filename}`}>
                                  <Download className="w-4 h-4" />
                                </a>
                              </Button>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bulletins Tab */}
        <TabsContent value="bulletins">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#F97316]" />
                Alerts & Bulletins
              </CardTitle>
            </CardHeader>
            <CardContent>
              {bulletins.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No bulletins for this project</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {bulletins.map((bulletin) => {
                    const isAcknowledged = bulletin.acknowledgements?.some(
                      (ack) => ack.user_id === user?.id
                    );
                    return (
                      <div 
                        key={bulletin.id}
                        className={`p-4 rounded-lg border-l-4 ${
                          bulletin.priority === 'high' 
                            ? 'border-l-red-500 bg-red-50' 
                            : 'border-l-[#F97316] bg-amber-50'
                        }`}
                        data-testid={`bulletin-${bulletin.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={bulletin.priority === 'high' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}>
                                {bulletin.priority}
                              </Badge>
                              <Badge variant="outline">{bulletin.bulletin_type}</Badge>
                            </div>
                            <h4 className="font-semibold">{bulletin.title}</h4>
                            <p className="text-sm mt-1">{bulletin.content}</p>
                            <p className="text-xs text-slate-500 mt-2">
                              Posted by {bulletin.created_by_name} • {new Date(bulletin.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          {bulletin.requires_acknowledgement && (
                            <div className="ml-4">
                              {isAcknowledged ? (
                                <Badge className="bg-green-100 text-green-800">
                                  <Check className="w-3 h-3 mr-1" />
                                  Acknowledged
                                </Badge>
                              ) : (
                                <Button 
                                  size="sm" 
                                  onClick={() => handleAcknowledgeBulletin(bulletin.id)}
                                  data-testid={`ack-bulletin-${bulletin.id}`}
                                >
                                  Acknowledge
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Weather Tab */}
        <TabsContent value="weather">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cloud className="w-5 h-5" />
                Weather Forecast
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!weather ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Current Weather */}
                  <div className="p-6 rounded-xl weather-clear text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-75">Current Conditions</p>
                        <p className="text-5xl font-bold">{weather.current?.temp || 72}°F</p>
                        <p className="text-lg mt-1">{weather.current?.weather?.[0]?.description || 'Clear sky'}</p>
                      </div>
                      <Cloud className="w-16 h-16 opacity-75" />
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-white/20">
                      <div>
                        <p className="text-xs opacity-75">Feels Like</p>
                        <p className="font-semibold">{weather.current?.feels_like || 70}°F</p>
                      </div>
                      <div>
                        <p className="text-xs opacity-75">Wind</p>
                        <p className="font-semibold">{weather.current?.wind_speed || 12} mph</p>
                      </div>
                      <div>
                        <p className="text-xs opacity-75">Humidity</p>
                        <p className="font-semibold">{weather.current?.humidity || 45}%</p>
                      </div>
                    </div>
                  </div>

                  {/* Warnings */}
                  {weather.warnings?.length > 0 && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <h4 className="font-semibold text-red-800 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Weather Warnings
                      </h4>
                      <ul className="mt-2 space-y-1">
                        {weather.warnings.map((warning, i) => (
                          <li key={i} className="text-sm text-red-700">{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* 7-day Forecast */}
                  <div>
                    <h4 className="font-semibold mb-3">7-Day Forecast</h4>
                    <div className="grid grid-cols-7 gap-2">
                      {weather.forecast?.map((day, i) => (
                        <div key={i} className="p-3 bg-slate-50 rounded-lg text-center">
                          <p className="text-xs text-slate-500">
                            {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                          </p>
                          <Cloud className="w-6 h-6 mx-auto my-2 text-slate-400" />
                          <p className="font-semibold text-sm">{day.high}°</p>
                          <p className="text-xs text-slate-500">{day.low}°</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ask Stewie Tab */}
        <TabsContent value="stewie">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="w-5 h-5 text-[#F97316]" />
                Ask Stewie - Safety & Union Reference
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Safety Briefing Section */}
              <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-amber-50 border border-red-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-red-600" />
                    <h3 className="font-bold text-red-800">Job Safety Briefing</h3>
                  </div>
                  <Button 
                    onClick={handleGenerateSafetyBriefing}
                    disabled={briefingLoading}
                    className="bg-red-600 hover:bg-red-700"
                    size="sm"
                  >
                    {briefingLoading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Shield className="w-4 h-4 mr-2" />
                        Generate Safety Briefing
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-sm text-red-700 mb-3">
                  Generate a job-specific safety briefing based on this work order&apos;s equipment type, location, and project details.
                </p>
                
                {safetyBriefing && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-red-100">
                    <div className="flex items-center justify-between mb-3 pb-2 border-b">
                      <span className="text-xs text-slate-500">
                        Generated: {new Date(safetyBriefing.generated_at).toLocaleString()}
                      </span>
                      <Badge className="bg-red-100 text-red-700">Safety Briefing</Badge>
                    </div>
                    <div className="prose prose-sm max-w-none">
                      <div className="whitespace-pre-wrap text-sm text-slate-700">
                        {safetyBriefing.briefing}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
                <p className="text-sm text-amber-800">
                  <strong>Ask Stewie</strong> answers questions based on the Safety Rule Book and Union Agreement. 
                  Responses include citations and page references.
                </p>
              </div>

              <form onSubmit={handleAskStewie} className="mb-6">
                <div className="flex gap-2">
                  <Input
                    placeholder="Ask about safety rules, union agreements, procedures..."
                    value={stewieQuery}
                    onChange={(e) => setStewieQuery(e.target.value)}
                    className="flex-1"
                    data-testid="stewie-input"
                  />
                  <Button 
                    type="submit" 
                    className="bg-[#F97316] hover:bg-[#EA580C]"
                    disabled={stewieLoading}
                    data-testid="stewie-submit"
                  >
                    {stewieLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </form>

              {stewieResponse && (
                <div className="p-4 bg-slate-50 rounded-lg" data-testid="stewie-response">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#F97316] rounded-full flex items-center justify-center flex-shrink-0">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="whitespace-pre-wrap">{stewieResponse.response}</p>
                      {stewieResponse.sources && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs text-slate-500">
                            Sources: {stewieResponse.sources.join(', ')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Questions - Dynamic based on work order */}
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-slate-600 mb-3">Quick Questions for This Job</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 overflow-hidden">
                  {(() => {
                    const questions = [];
                    const equipType = workOrder.equipment_type?.toLowerCase() || '';
                    const projectType = workOrder.project_type?.toLowerCase() || '';
                    const location = workOrder.location_name || '';
                    
                    // Equipment-specific questions
                    if (equipType.includes('transformer')) {
                      questions.push(
                        `Oil handling safety for ${workOrder.equipment_type}?`,
                        `Confined space entry for transformer work?`,
                        `PPE for transformer maintenance?`
                      );
                    }
                    if (equipType.includes('breaker') || equipType.includes('circuit')) {
                      questions.push(
                        `Arc flash PPE for breaker work?`,
                        `Racking procedures for circuit breakers?`,
                        `SF6 gas handling safety?`
                      );
                    }
                    if (equipType.includes('switch') || equipType.includes('disconnect')) {
                      questions.push(
                        `Switching sequence requirements?`,
                        `Minimum approach distance for this voltage?`
                      );
                    }
                    if (equipType.includes('relay') || equipType.includes('protection')) {
                      questions.push(
                        `Relay testing isolation procedures?`,
                        `Two-person rule for protection work?`
                      );
                    }
                    
                    // Project type questions
                    if (projectType.includes('replacement') || projectType.includes('install')) {
                      questions.push(
                        `Equipment grounding before removal?`,
                        `Clearance procedures for new equipment?`
                      );
                    }
                    if (projectType.includes('test') || projectType.includes('maintenance')) {
                      questions.push(
                        `LOTO requirements for testing?`,
                        `Energized work permit needed?`
                      );
                    }
                    if (projectType.includes('emergency')) {
                      questions.push(
                        `Emergency switching authority?`,
                        `Hot work procedures?`
                      );
                    }
                    
                    // General questions based on work order
                    questions.push(
                      `Safety rules for work at ${location}?`,
                      `Overtime rules for this project?`,
                      `Required crew certifications?`
                    );
                    
                    // Limit to 8 questions
                    return questions.slice(0, 8).map((q, i) => (
                      <Button
                        key={i}
                        variant="outline"
                        size="sm"
                        onClick={() => setStewieQuery(q)}
                        className="text-left h-auto py-2 justify-start truncate max-w-full overflow-hidden"
                      >
                        <span className="truncate">{q}</span>
                      </Button>
                    ));
                  })()}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Project Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              {events.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No events recorded yet</p>
                </div>
              ) : (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
                  <div className="space-y-4">
                    {events.map((event) => (
                      <div 
                        key={event.id} 
                        className="flex gap-4 relative"
                        data-testid={`event-${event.id}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-[#0056D2] flex items-center justify-center z-10">
                          <div className="w-2 h-2 bg-white rounded-full" />
                        </div>
                        <div className="flex-1 pb-4">
                          <p className="font-medium">{event.description}</p>
                          <p className="text-sm text-slate-500">
                            {new Date(event.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Crew / Quick Canvass Tab */}
        <TabsContent value="crew">
          <div className="space-y-6">
            {/* Crew Size Summary */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#0056D2]" />
                  Crew Assignment
                </CardTitle>
                {isSupervisor() && (
                  <div className="flex gap-2">
                    {workOrder.qc_crew_id ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleUnassignCrew}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <UserX className="w-4 h-4 mr-1" />
                        Unassign Crew
                      </Button>
                    ) : null}
                    <Button 
                      size="sm"
                      onClick={() => {
                        fetchAvailableCrews();
                        setShowAssignCrewDialog(true);
                      }}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="assign-crew-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Assign Crew from Canvass
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-[#0056D2]">
                      {(workOrder.assigned_crew?.length || 0) + (workOrder.crew_leader_id ? 1 : 0)}
                    </p>
                    <p className="text-sm text-slate-600">Total Crew Size</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-green-600">
                      {canvassRequests.filter(r => r.assignments?.some(a => a.accepted)).length}
                    </p>
                    <p className="text-sm text-slate-600">Accepted Assignments</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-amber-600">
                      {canvassRequests.filter(r => r.status === 'pending').length}
                    </p>
                    <p className="text-sm text-slate-600">Pending Requests</p>
                  </div>
                </div>

                {/* Assigned Crew from Quick Canvass */}
                {workOrder.qc_crew_name && (
                  <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-purple-800 flex items-center gap-2">
                        <UserCheck className="w-5 h-5" />
                        Assigned Quick Canvass Crew
                      </h4>
                      <Badge className="bg-purple-600">{workOrder.qc_crew_name}</Badge>
                    </div>
                    {workOrder.assigned_crew_details?.length > 0 ? (
                      <div className="space-y-2">
                        {workOrder.assigned_crew_details.map((member, i) => (
                          <div key={i} className="flex items-center gap-3 p-2 bg-white rounded border border-purple-100">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                              member.position?.toLowerCase().includes('leader') ? 'bg-purple-600' : 'bg-purple-400'
                            }`}>
                              {member.name?.charAt(0) || '?'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{member.name}</p>
                              <p className="text-xs text-purple-600">{member.position}</p>
                            </div>
                            {member.position?.toLowerCase().includes('leader') && (
                              <Badge variant="outline" className="border-purple-300 text-purple-700">Leader</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-purple-700">
                        Crew assigned: {(workOrder.assigned_crew?.length || 0) + (workOrder.crew_leader_id ? 1 : 0)} members
                      </p>
                    )}
                    <p className="text-xs text-purple-500 mt-2">
                      Assigned: {workOrder.crew_assigned_at ? new Date(workOrder.crew_assigned_at).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                )}

                {/* Current Crew (non-canvass) */}
                {!workOrder.qc_crew_name && (
                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">Current Crew</h4>
                    {workOrder.crew_leader_id || workOrder.assigned_crew?.length > 0 ? (
                      <div className="space-y-2">
                        {workOrder.crew_leader_id && (
                          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                            <div className="w-10 h-10 bg-[#0056D2] rounded-full flex items-center justify-center text-white font-bold">
                              <UserCheck className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="font-medium">Crew Leader</p>
                              <p className="text-sm text-slate-500">Assigned</p>
                            </div>
                            <Badge className="ml-auto bg-[#0056D2]">Leader</Badge>
                          </div>
                        )}
                        {workOrder.assigned_crew?.map((crewId, i) => (
                          <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">
                              <Users className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="font-medium">Crew Member {i + 1}</p>
                              <p className="text-sm text-slate-500">Assigned</p>
                            </div>
                            <Badge variant="outline" className="ml-auto">Member</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-slate-500 border border-dashed rounded-lg">
                        <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>No crew assigned yet</p>
                        {isSupervisor() && (
                          <p className="text-xs mt-1">Click &quot;Assign Crew from Canvass&quot; to assign a crew</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Canvass Staffing Request */}
            <Card className="border-2 border-purple-200">
              <CardHeader className="bg-purple-50">
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-purple-600" />
                    Quick Canvass Staffing Request
                  </span>
                  <a 
                    href="https://www.quickcanvass.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-purple-600 hover:underline flex items-center gap-1"
                  >
                    Open Quick Canvass <ExternalLink className="w-4 h-4" />
                  </a>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {/* Staffing Request Form */}
                {!showStaffingRequestForm ? (
                  <div className="text-center py-6">
                    <UserPlus className="w-12 h-12 mx-auto mb-3 text-purple-400" />
                    <p className="text-purple-700 font-medium mb-2">Need additional crew members?</p>
                    <p className="text-sm text-purple-600 mb-4">
                      Submit a staffing request to Quick Canvass for crew assignment.
                    </p>
                    <Button 
                      onClick={() => setShowStaffingRequestForm(true)}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="new-staffing-request-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      New Staffing Request
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <h4 className="font-semibold text-purple-800 mb-3">Positions Needed</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {/* Crew Leader */}
                        <div className="text-center">
                          <Label className="text-sm text-purple-700">Crew Leader</Label>
                          <div className="flex items-center justify-center gap-2 mt-1">
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setStaffingRequest(prev => ({
                                ...prev, 
                                crew_leader: Math.max(0, prev.crew_leader - 1)
                              }))}
                            >-</Button>
                            <span className="w-8 text-center font-bold text-lg">{staffingRequest.crew_leader}</span>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setStaffingRequest(prev => ({
                                ...prev, 
                                crew_leader: prev.crew_leader + 1
                              }))}
                            >+</Button>
                          </div>
                        </div>
                        
                        {/* A Mechanic */}
                        <div className="text-center">
                          <Label className="text-sm text-purple-700">A Mechanic</Label>
                          <div className="flex items-center justify-center gap-2 mt-1">
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setStaffingRequest(prev => ({
                                ...prev, 
                                a_mechanic: Math.max(0, prev.a_mechanic - 1)
                              }))}
                            >-</Button>
                            <span className="w-8 text-center font-bold text-lg">{staffingRequest.a_mechanic}</span>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setStaffingRequest(prev => ({
                                ...prev, 
                                a_mechanic: prev.a_mechanic + 1
                              }))}
                            >+</Button>
                          </div>
                        </div>
                        
                        {/* B Mechanic */}
                        <div className="text-center">
                          <Label className="text-sm text-purple-700">B Mechanic</Label>
                          <div className="flex items-center justify-center gap-2 mt-1">
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setStaffingRequest(prev => ({
                                ...prev, 
                                b_mechanic: Math.max(0, prev.b_mechanic - 1)
                              }))}
                            >-</Button>
                            <span className="w-8 text-center font-bold text-lg">{staffingRequest.b_mechanic}</span>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setStaffingRequest(prev => ({
                                ...prev, 
                                b_mechanic: prev.b_mechanic + 1
                              }))}
                            >+</Button>
                          </div>
                        </div>
                        
                        {/* Helper */}
                        <div className="text-center">
                          <Label className="text-sm text-purple-700">Helper</Label>
                          <div className="flex items-center justify-center gap-2 mt-1">
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setStaffingRequest(prev => ({
                                ...prev, 
                                helper: Math.max(0, prev.helper - 1)
                              }))}
                            >-</Button>
                            <span className="w-8 text-center font-bold text-lg">{staffingRequest.helper}</span>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setStaffingRequest(prev => ({
                                ...prev, 
                                helper: prev.helper + 1
                              }))}
                            >+</Button>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4 text-center">
                        <Badge className="bg-purple-600">
                          Total: {staffingRequest.crew_leader + staffingRequest.a_mechanic + 
                                  staffingRequest.b_mechanic + staffingRequest.helper} positions
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Shift Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-purple-700">Shift Start</Label>
                        <Input 
                          type="datetime-local"
                          value={staffingRequest.shift_start}
                          onChange={(e) => setStaffingRequest(prev => ({...prev, shift_start: e.target.value}))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-purple-700">Shift End</Label>
                        <Input 
                          type="datetime-local"
                          value={staffingRequest.shift_end}
                          onChange={(e) => setStaffingRequest(prev => ({...prev, shift_end: e.target.value}))}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    
                    {/* Notes */}
                    <div>
                      <Label className="text-sm text-purple-700">Notes (optional)</Label>
                      <Input 
                        placeholder="Any special requirements or notes..."
                        value={staffingRequest.notes}
                        onChange={(e) => setStaffingRequest(prev => ({...prev, notes: e.target.value}))}
                        className="mt-1"
                      />
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex justify-end gap-2 pt-2">
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setShowStaffingRequestForm(false);
                          setStaffingRequest({
                            crew_leader: 0,
                            a_mechanic: 0,
                            b_mechanic: 0,
                            helper: 0,
                            shift_start: '',
                            shift_end: '',
                            notes: ''
                          });
                        }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleSubmitStaffingRequest}
                        disabled={submittingStaffing}
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid="submit-staffing-request-btn"
                      >
                        {submittingStaffing ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Submit Request
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Canvass Request History */}
                <div className="mt-6">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Staffing Request History
                  </h4>
                  {canvassRequests.length === 0 ? (
                    <div className="text-center py-6 text-slate-500 border border-dashed rounded-lg">
                      <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>No staffing requests yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {canvassRequests.map((request) => (
                        <div 
                          key={request.id}
                          className="p-4 border rounded-lg"
                          data-testid={`canvass-${request.id}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <Badge className={
                                request.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                                request.status === 'filled' ? 'bg-green-100 text-green-800' :
                                'bg-slate-100 text-slate-800'
                              }>
                                {request.status}
                              </Badge>
                              <p className="text-sm text-slate-500 mt-1">
                                {new Date(request.created_at).toLocaleString()}
                              </p>
                            </div>
                            <div className="text-right text-sm">
                              <p className="font-medium">{request.shift_start} - {request.shift_end}</p>
                              <p className="text-slate-500">{request.required_positions?.length || 0} positions</p>
                            </div>
                          </div>
                          
                          {request.required_positions?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {request.required_positions.map((pos, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{pos}</Badge>
                              ))}
                            </div>
                          )}

                          {request.assignments?.length > 0 && (
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-xs text-slate-500 mb-2">Responses:</p>
                              <div className="space-y-1">
                                {request.assignments.map((assignment, i) => (
                                  <div key={i} className="flex items-center gap-2 text-sm">
                                    {assignment.accepted ? (
                                      <UserCheck className="w-4 h-4 text-green-500" />
                                    ) : (
                                      <UserX className="w-4 h-4 text-red-500" />
                                    )}
                                    <span>{assignment.user_name}</span>
                                    <span className="text-slate-400">- {assignment.position}</span>
                                    <Badge variant="outline" className={assignment.accepted ? 'border-green-300 text-green-700' : 'border-red-300 text-red-700'}>
                                      {assignment.accepted ? 'Accepted' : 'Declined'}
                                    </Badge>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Assign Crew Dialog */}
                <Dialog open={showAssignCrewDialog} onOpenChange={setShowAssignCrewDialog}>
                  <DialogContent className="max-w-lg bg-slate-900 border-slate-700 text-white">
                    <DialogHeader>
                      <DialogTitle className="text-purple-400 flex items-center gap-2">
                        <UserPlus className="w-5 h-5" />
                        Assign Crew from Quick Canvass
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <p className="text-sm text-slate-300">
                        Select a crew established through Quick Canvass to assign to this work order.
                      </p>
                      
                      {availableCrews.length === 0 ? (
                        <div className="text-center py-8 text-slate-400">
                          <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p className="font-medium">No Available Crews</p>
                          <p className="text-sm mt-1">
                            Create a canvass request in Quick Canvass first to establish crews.
                          </p>
                        </div>
                      ) : (
                        <ScrollArea className="h-[300px]">
                          <div className="space-y-2 pr-4">
                            {availableCrews.map((crew) => (
                              <div 
                                key={crew.id}
                                className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                                  selectedCrew?.id === crew.id 
                                    ? 'border-purple-500 bg-purple-900/30' 
                                    : 'border-slate-600 bg-slate-800 hover:border-purple-400'
                                }`}
                                onClick={() => setSelectedCrew(crew)}
                                data-testid={`crew-option-${crew.id}`}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="font-semibold text-purple-300">{crew.name}</h4>
                                  <Badge className={crew.status === 'available' ? 'bg-green-600 text-white' : 'bg-amber-600 text-white'}>
                                    {crew.status}
                                  </Badge>
                                </div>
                                <div className="flex flex-wrap gap-1 mb-2">
                                  {crew.members?.map((member, i) => (
                                    <Badge key={i} variant="outline" className="text-xs border-slate-500 text-slate-300">
                                      {member.user_name || member.name || `Member ${i+1}`} - {member.position}
                                    </Badge>
                                  ))}
                                </div>
                                <p className="text-xs text-slate-400">
                                  {crew.members?.length || 0} members • Source: {crew.source}
                                </p>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      )}

                      <div className="flex justify-end gap-2 pt-4 border-t border-slate-700">
                        <Button 
                          variant="outline" 
                          onClick={() => {
                            setShowAssignCrewDialog(false);
                            setSelectedCrew(null);
                          }}
                          className="border-slate-600 hover:bg-slate-700"
                        >
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleAssignCrew}
                          disabled={!selectedCrew || assigningCrew}
                          className="bg-purple-600 hover:bg-purple-700"
                          data-testid="confirm-assign-crew-btn"
                        >
                          {assigningCrew ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Assigning...
                            </>
                          ) : (
                            <>
                              <UserCheck className="w-4 h-4 mr-2" />
                              Assign Crew
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>

            {/* Crew Requirements Panel - For Quick Canvass Integration */}
            {isSupervisor() && (
              <CrewRequirementsPanel 
                workOrderId={id}
                workOrder={workOrder}
                onUpdate={fetchWorkOrder}
              />
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Download Dialog */}
      <DownloadDialog
        open={showDownloadDialog}
        onOpenChange={setShowDownloadDialog}
        fileType={downloadDialogConfig.fileType}
        defaultFilename={downloadDialogConfig.defaultFilename}
        fileSize={downloadDialogConfig.fileSize}
        onDownload={downloadDialogConfig.onDownload}
      />

      {/* Daily Progress Form */}
      <DailyProgressForm
        open={showDailyProgressForm}
        onOpenChange={setShowDailyProgressForm}
        workOrder={workOrder}
        onSuccess={handleDailyProgressSuccess}
      />

      {/* Lesson Viewer Dialog */}
      <Dialog open={showLessonDialog} onOpenChange={setShowLessonDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-400">
              <Lightbulb className="w-5 h-5" />
              Lesson Learned
            </DialogTitle>
          </DialogHeader>
          {selectedLesson && (
            <div className="space-y-4">
              {/* Title */}
              <div>
                <h2 className="text-xl font-bold text-white">{selectedLesson.title}</h2>
                {selectedLesson.category && (
                  <Badge className="mt-2 bg-emerald-600 text-white">
                    {selectedLesson.category}
                  </Badge>
                )}
              </div>
              
              {/* Full Content */}
              <div className="p-4 bg-emerald-900/30 rounded-lg border border-emerald-700">
                <div className="prose prose-sm max-w-none text-emerald-200 whitespace-pre-wrap">
                  {selectedLesson.content}
                </div>
              </div>
              
              {/* Outcome/Resolution (if available) */}
              {selectedLesson.outcome && (
                <div className="p-4 bg-blue-900/30 rounded-lg border border-blue-700">
                  <h4 className="font-semibold text-blue-400 mb-2">Outcome / Resolution</h4>
                  <div className="prose prose-sm max-w-none text-blue-200 whitespace-pre-wrap">
                    {selectedLesson.outcome}
                  </div>
                </div>
              )}
              
              {/* Tags (if available) */}
              {selectedLesson.tags && selectedLesson.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedLesson.tags.map((tag, idx) => (
                    <Badge key={idx} variant="outline" className="bg-slate-800 text-slate-300 border-slate-600">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Additional Info */}
              {(selectedLesson.project_type || selectedLesson.asset_type) && (
                <div className="flex flex-wrap gap-4 text-sm text-slate-300 p-3 bg-slate-800 rounded-lg">
                  {selectedLesson.project_type && (
                    <div>
                      <span className="font-medium text-white">Project Type:</span> {selectedLesson.project_type}
                    </div>
                  )}
                  {selectedLesson.asset_type && (
                    <div>
                      <span className="font-medium text-white">Asset Type:</span> {selectedLesson.asset_type}
                    </div>
                  )}
                </div>
              )}
              
              {/* Metadata */}
              <div className="flex flex-wrap gap-4 text-sm text-slate-400 pt-2 border-t border-slate-700">
                {selectedLesson.project_name && (
                  <div>
                    <span className="font-medium text-white">Project:</span> {selectedLesson.project_name}
                  </div>
                )}
                {selectedLesson.created_at && (
                  <div>
                    <span className="font-medium text-white">Added:</span> {new Date(selectedLesson.created_at).toLocaleDateString()}
                  </div>
                )}
                {selectedLesson.created_by_name && (
                  <div>
                    <span className="font-medium text-white">By:</span> {selectedLesson.created_by_name}
                  </div>
                )}
              </div>
              
              {/* Close Button */}
              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={() => setShowLessonDialog(false)} className="border-slate-600 hover:bg-slate-700">
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mobile Sticky Submit Daily Progress Button */}
      {canSubmitDailyProgress() && !todayProgressReport && (
        <div className="fixed bottom-4 left-4 right-4 z-50 md:hidden">
          <Button
            onClick={() => setShowDailyProgressForm(true)}
            className="w-full h-14 text-base bg-blue-600 hover:bg-blue-700 shadow-lg rounded-xl"
          >
            <ClipboardCheck className="w-5 h-5 mr-2" />
            Submit Daily Progress
          </Button>
        </div>
      )}
    </div>
  );
};

export default WorkOrderDetailPage;
